import { supabase } from '@/integrations/supabase/supabase';

const PAGE_SIZE = 500;
const MAX_CONSISTENCY_RESTARTS = 2;
const THREADS_SELECT = `
  thread_id,
  created_at,
  user_id,
  conversation,
  users!inner(client_id)
`;

const queryError = (error) => {
  if (error instanceof Error) return error;
  return new Error(error?.message || 'Could not load tenant threads.');
};

class ThreadCountConsistencyError extends Error {}

const throwIfAborted = (signal) => {
  if (!signal?.aborted) return;
  throw signal.reason ?? new DOMException('The operation was aborted.', 'AbortError');
};

const parseThread = (thread, clientId) => {
  const threadId = thread?.thread_id;
  if (typeof threadId !== 'string' || threadId.trim() === '') {
    throw new Error('Threads query returned an invalid thread_id.');
  }
  if (thread?.users?.client_id !== clientId) {
    throw new Error('Threads query returned a row from another tenant.');
  }

  const conversation = thread.conversation
    ? (typeof thread.conversation === 'string'
        ? JSON.parse(thread.conversation)
        : thread.conversation)
    : [];
  if (!Array.isArray(conversation)) {
    throw new Error('Threads query returned a conversation that is not an array.');
  }

  return {
    ...thread,
    conversation,
  };
};

const assertExactCount = (count) => {
  if (!Number.isSafeInteger(count) || count < 0) {
    throw new Error('Threads query did not return an exact count.');
  }
};

const fetchHeadcount = async (client, clientId, signal) => {
  let query = client
    .from('threads')
    .select('thread_id, users!inner(client_id)', { count: 'exact', head: true })
    .eq('users.client_id', clientId);

  if (signal) query = query.abortSignal(signal);

  const { count, error } = await query;
  if (error) throw queryError(error);
  assertExactCount(count);
  return count;
};

/**
 * Loads every thread belonging to a tenant through an ordered keyset traversal.
 * Results are returned only after every expected row has been read and rechecked.
 */
const loadAllThreadsOnce = async (clientId, { signal, client }) => {

  const threads = [];
  const seenIds = new Set();
  let expectedCount;
  let cursor;
  let pageCount = 0;

  while (expectedCount === undefined || threads.length < expectedCount) {
    // A provider may cap a requested page below PAGE_SIZE. Every non-empty page
    // must still add a unique row, so this remains a finite safety bound.
    if (expectedCount !== undefined && pageCount >= expectedCount + 1) {
      throw new Error('Threads query exceeded its safe traversal bound.');
    }

    let query = client
      .from('threads')
      .select(THREADS_SELECT, { count: 'exact' })
      .eq('users.client_id', clientId)
      .order('thread_id', { ascending: true })
      .limit(PAGE_SIZE);

    if (cursor !== undefined) query = query.gt('thread_id', cursor);
    if (signal) query = query.abortSignal(signal);

    const { data, count, error } = await query;
    if (error) throw queryError(error);
    assertExactCount(count);

    if (expectedCount === undefined) {
      expectedCount = count;
    } else if (threads.length + count !== expectedCount) {
      throw new ThreadCountConsistencyError('Tenant thread count changed during pagination.');
    }

    if (!Array.isArray(data)) {
      throw new Error('Threads query returned malformed data.');
    }
    if (data.length === 0 && threads.length < expectedCount) {
      throw new Error('Threads query made no pagination progress.');
    }

    for (const rawThread of data) {
      const thread = parseThread(rawThread, clientId);
      if (seenIds.has(thread.thread_id)) {
        throw new Error('Threads query returned a duplicate thread_id.');
      }
      seenIds.add(thread.thread_id);
      threads.push(thread);
    }

    if (threads.length > expectedCount) {
      throw new Error('Threads query returned more rows than its exact count.');
    }
    if (data.length > 0) {
      const nextCursor = data[data.length - 1]?.thread_id;
      if (nextCursor === cursor) {
        throw new Error('Threads query made no pagination progress.');
      }
      cursor = nextCursor;
    }
    pageCount += 1;
  }

  if (threads.length !== expectedCount) {
    throw new Error('Threads query ended before all tenant threads were loaded.');
  }

  const finalHeadcount = await fetchHeadcount(client, clientId, signal);
  if (finalHeadcount !== expectedCount) {
    throw new ThreadCountConsistencyError('Tenant thread count changed before pagination completed.');
  }

  return threads;
};

/**
 * Loads every thread belonging to a tenant through an ordered keyset traversal.
 * A traversal only restarts for the two count checks that prove a concurrent
 * write made that traversal inconsistent. All other failures are surfaced at
 * once so malformed or unauthorized data cannot be hidden by a retry.
 */
export const fetchAllThreadsFromClient = async (
  clientId,
  { signal, client = supabase } = {},
) => {
  if (!clientId) return [];

  for (let attempt = 0; attempt <= MAX_CONSISTENCY_RESTARTS; attempt += 1) {
    throwIfAborted(signal);
    try {
      const threads = await loadAllThreadsOnce(clientId, { signal, client });
      throwIfAborted(signal);
      return threads;
    } catch (error) {
      throwIfAborted(signal);
      if (!(error instanceof ThreadCountConsistencyError) || attempt === MAX_CONSISTENCY_RESTARTS) {
        throw error;
      }
    }
  }

  throw new Error('Threads query exhausted its consistency retries.');
};
